import ExcelJS from "exceljs";

/**
 * Excel I/O for the admin bulk-import flows. Wraps `exceljs` so the dialog
 * code (Users page + Attendees page) doesn't need to know the library
 * specifics, and a future swap (e.g. to a thinner package) only touches
 * this file.
 *
 * Library choice: `exceljs` over `xlsx` (SheetJS) because the npm-published
 * SheetJS build (`xlsx@0.18.5`) carries unfixed prototype-pollution and
 * ReDoS advisories; SheetJS moved active maintenance to their own CDN,
 * leaving the registry version stale. `exceljs` has a transitive uuid
 * advisory that is theoretically reachable only via uuid.v3/v5/v6 with a
 * caller-provided buffer — `exceljs` only calls `uuid.v4()`, so the
 * advisory is unreachable in practice.
 */

export interface ExcelSheetSpec {
  /** Column headers for the template, in display order. */
  headers: string[];
  /** Optional example rows shown beneath the header for orientation. */
  exampleRows?: Array<Record<string, string | number | null>>;
  /** Sheet name. Defaults to "Sheet1". */
  sheetName?: string;
}

/**
 * Triggers a download of a single-sheet xlsx template in the user's
 * browser. Headers in row 1, example rows in 2..N. Caller is responsible
 * for picking a sensible filename (suggested: `${kind}-import-template.xlsx`).
 */
export async function downloadExcelTemplate(
  filename: string,
  spec: ExcelSheetSpec,
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(spec.sheetName ?? "Sheet1");

  sheet.columns = spec.headers.map((header) => ({
    header,
    key: header,
    width: Math.max(12, header.length + 2),
  }));

  if (spec.exampleRows?.length) {
    for (const row of spec.exampleRows) {
      sheet.addRow(row);
    }
  }

  // Bold the header row for readability.
  sheet.getRow(1).font = { bold: true };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Reads an .xlsx file from a File input and returns the chosen sheet as a
 * `{ headers, rows }` pair. Each row is a header→cell-value object, with
 * empty/missing cells normalized to "" for stable downstream parsing.
 *
 * Two upload shapes are supported:
 *
 *   1. **Single-sheet** (default) — row 1 = headers, row 2+ = data.
 *      Kept for backwards-compatible per-entity imports.
 *   2. **Canonical organizer Planning Workbook** — multi-sheet xlsx
 *      shipped by `buildOrganizerWorkbook.ts`. Row 1 = purpose blurb
 *      merged across all columns, row 2 = headers, rows 3–4 = column
 *      notes + example data (skipped), row 5+ = real data. Pass
 *      `sheetName` so the reader picks the right sheet by name; layout
 *      is auto-detected via the merged-A1 signature.
 *
 * Files without a header row (just a single column of values) are
 * supported: pass `{ headerless: true }` and the first column's cells are
 * returned under the synthetic key set in `headerlessKey` (defaults to
 * "value"). Used by the email-only attendee bulk import where the file is
 * literally a column of email addresses.
 */
export interface ReadExcelOptions {
  /**
   * Treat the first row as data (no header). Single-column shape. Useful
   * for one-column email files.
   */
  headerless?: boolean;
  /** Synthetic key when `headerless: true`. Default "value". */
  headerlessKey?: string;
  /**
   * Find the worksheet whose name matches (case-insensitive, trimmed).
   * Single-sheet uploads may use any sheet name; multi-sheet uploads must
   * contain the requested sheet.
   */
  sheetName?: string;
  /**
   * Sheet layout. Defaults to auto-detection:
   *   - **simple** — row 1 = headers, row 2+ = data.
   *   - **canonical** — row 1 = merged purpose blurb, row 2 = headers,
   *     rows 3–4 = column notes + example (skipped), row 5+ = data.
   *     Used by the organizer Planning Workbook
   *     (`event-planner-templates.xlsx`).
   *
   * Auto-detection peek: when row 1 has a merge that starts at A1,
   * the reader picks `canonical`. Single-sheet template uploads never
   * have a merged A1, so they stay on `simple` for free.
   */
  layout?: "simple" | "canonical";
}

const HEADER_ROW_BY_LAYOUT: Record<"simple" | "canonical", number> = {
  simple: 1,
  canonical: 2,
};

const DATA_START_ROW_BY_LAYOUT: Record<"simple" | "canonical", number> = {
  simple: 2,
  canonical: 5,
};

export async function readExcelRows<T extends Record<string, string | undefined>>(
  file: File,
  options: ReadExcelOptions = {},
): Promise<{ headers: string[]; rows: T[] }> {
  const buffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = pickSheet(workbook, options.sheetName);
  if (!sheet) return { headers: [], rows: [] };

  // Headerless files: emit synthetic single-column rows. Treats every
  // non-empty cell in the first column as a value.
  if (options.headerless) {
    const key = options.headerlessKey ?? "value";
    const rows: T[] = [];
    sheet.eachRow({ includeEmpty: false }, (row) => {
      const cell = row.getCell(1);
      const value = cellToString(cell.value);
      if (value) rows.push({ [key]: value } as T);
    });
    return { headers: [key], rows };
  }

  const layout = options.layout ?? detectLayout(sheet);
  const headerRowNum = HEADER_ROW_BY_LAYOUT[layout];
  const dataStartRow = DATA_START_ROW_BY_LAYOUT[layout];

  const headerRow = sheet.getRow(headerRowNum);
  const headers: string[] = [];
  headerRow.eachCell((cell) => {
    headers.push(cellToString(cell.value).trim());
  });

  const rows: T[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber < dataStartRow) return; // skip header + canonical preamble
    const obj: Record<string, string> = {};
    headers.forEach((header, idx) => {
      const cell = row.getCell(idx + 1);
      obj[header] = cellToString(cell.value);
    });
    // Skip rows that are entirely empty (defensive against trailing blank
    // rows Excel sometimes leaves behind).
    if (Object.values(obj).every((v) => !v)) return;
    rows.push(obj as T);
  });

  return { headers, rows };
}

/**
 * Find the worksheet whose name matches `name` (case-insensitive, trimmed).
 * Single-sheet uploads may use any sheet name. Multi-sheet workbooks are
 * strict: silently falling back to the first sheet makes wrong-dialog uploads
 * look like mysterious row validation failures.
 */
function pickSheet(
  workbook: ExcelJS.Workbook,
  name: string | undefined,
): ExcelJS.Worksheet | undefined {
  if (!name) return workbook.worksheets[0];
  const target = name.trim().toLowerCase();
  const matched = workbook.worksheets.find(
    (ws) => ws.name.trim().toLowerCase() === target,
  );
  if (matched) return matched;
  if (workbook.worksheets.length === 1) return workbook.worksheets[0];
  throw new Error(`Sheet "${name}" was not found in this workbook.`);
}

/**
 * Detect canonical Planning Workbook layout. Two signals — either is
 * sufficient because single-column canonical sheets (like Roster) have
 * no real merge to detect.
 *
 * 1. **Merged A1 row** — `buildOrganizerWorkbook.ts` always merges row 1
 *    across all columns to hold the purpose blurb. Multi-column canonical
 *    sheets (Users, Sessions, Travel kinds) all hit this signal.
 * 2. **Green tab color (FF4CAF50)** — every import-ready canonical sheet
 *    is tab-colored green during generation. Catches single-column
 *    canonical sheets where the merge degenerates. Single-sheet template
 *    downloads via `downloadExcelTemplate` never set a tab color.
 *
 * False-positive risk is low: a hand-crafted xlsx would have to set the
 * exact green ARGB AND lay out a purpose blurb on row 1 to land on
 * canonical-by-accident. Either signal alone is enough to skip rows 1–4.
 */
function detectLayout(sheet: ExcelJS.Worksheet): "simple" | "canonical" {
  const merges = (sheet as unknown as { model?: { merges?: string[] } })
    .model?.merges;
  if (merges?.some((range) => /^a1:/i.test(range))) return "canonical";

  const tabColor = sheet.properties?.tabColor?.argb;
  if (tabColor && tabColor.toUpperCase() === "FF4CAF50") return "canonical";

  return "simple";
}

/**
 * Coerce an exceljs cell value to a string. Cell values can be strings,
 * numbers, booleans, dates, formulas (`{ result }`), or rich-text objects
 * (`{ richText: [...] }`). Everything that isn't usefully stringifiable
 * yields "".
 */
function cellToString(value: ExcelJS.CellValue): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "object") {
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join("");
    }
    if ("result" in value && value.result !== undefined) {
      return cellToString(value.result as ExcelJS.CellValue);
    }
    if ("text" in value) {
      return cellToString((value as { text: ExcelJS.CellValue }).text);
    }
  }
  return "";
}
